// Installable-window support (roadmap #100): wraps the browser's beforeinstallprompt
// event so the staff dashboard can offer an "Install application" affordance without
// pulling in a UI library. Only ever imported from the staff surface (see
// StaffDashboard.tsx) — the public guest call flow never installs anything.
//
// Feature detection over browser sniffing: 'onbeforeinstallprompt' in window is an
// IDL attribute Chromium-based browsers (Chrome, Edge) define whether or not the
// event has actually fired yet; Firefox and Safari do not define it at all. That is
// the one reliable, technology-neutral signal available for "can this browser ever
// offer an install prompt" — actually getting a prompt still depends on the event
// firing, which additionally requires the browser's own installability heuristics
// (manifest fetched, some engagement, not already installed, etc.).
import { useEffect, useState } from 'react'

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

// Exported for the bootstrap's standalone-launch redirect (see main.tsx): a window
// running in standalone display-mode is always a staff window (guests never install).
export const isStandalone = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true)

export const installPromptSupported = typeof window !== 'undefined' && 'onbeforeinstallprompt' in window

export interface InstallPromptState {
  // Whether the affordance should render at all: either a captured prompt is ready to
  // use, or the browser has no installation support at all and needs the fallback.
  showAffordance: boolean
  // A native prompt is ready; clicking triggers it directly instead of the fallback.
  canPromptNatively: boolean
  installed: boolean
  promptInstall: () => Promise<void>
}

export const useInstallPrompt = (): InstallPromptState => {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(isStandalone)

  useEffect(() => {
    if (installed) return undefined

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      setDeferredEvent(event as BeforeInstallPromptEvent)
    }
    const onAppInstalled = () => {
      setInstalled(true)
      setDeferredEvent(null)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [installed])

  const promptInstall = async () => {
    if (!deferredEvent) return
    await deferredEvent.prompt()
    const choice = await deferredEvent.userChoice
    if (choice.outcome === 'accepted') setInstalled(true)
    setDeferredEvent(null)
  }

  return {
    showAffordance: !installed && (Boolean(deferredEvent) || !installPromptSupported),
    canPromptNatively: Boolean(deferredEvent),
    installed,
    promptInstall,
  }
}
